// Convex query/WS runs on <deployment>.convex.cloud; the HTTP router (webhook +
// manifest callback) runs on <deployment>.convex.site. Derive one from the other
// so the manifest can pre-fill both URLs with no extra env var.
export function convexSiteBase(convexUrl: string): string {
  return convexUrl.replace(".convex.cloud", ".convex.site");
}

export interface AppManifest {
  name: string;
  url: string;
  hook_attributes: { url: string };
  redirect_url: string;
  // Post-INSTALL redirect: GitHub sends the user here after they install the App
  // on their repos. The App must be CREATED with it — a manifest can't be
  // edited afterwards, which is exactly why this must NOT be an origin-specific
  // URL (see the setup_url note on buildAppManifest).
  setup_url: string;
  public: boolean;
  default_permissions: Record<string, string>;
  default_events: string[];
}

// The pre-filled GitHub App manifest. Permissions/events match exactly what the
// webhook dispatcher consumes (pull_request scoring + installation reconcile).
//
// URLs: the webhook, the manifest-callback AND the install-callback all live on
// the public convex.site (so GitHub can always reach them). The App's
// public-facing homepage uses `homepageUrl` (the product site) rather than the
// desktop's localhost origin — a GitHub App's homepage must be a stable public
// URL.
//
// `setup_url` used to be `${appUrl}/api/integrations/github/install-callback`,
// i.e. the origin the operator happened to be sitting on when they created the
// App — and a manifest-created App's URLs cannot be edited afterwards. Create
// the App from the desktop and setup_url freezes to http://127.0.0.1:3137/…,
// which nobody on the hosted web app can reach; create it from the web and it
// freezes to a path Cloudflare answers with the SPA fallback, which silently
// drops the installation_id and the state token. Either way one whole audience
// could never finish an install, permanently, with no way to fix it short of
// deleting the App.
//
// convex.site is the one origin that is public, always up, and identical for
// every client, so the post-install landing goes there
// (convex/http.ts → /integrations/github/install-callback). The origin to come
// BACK to is no longer part of the App at all: the install-state token records
// it (github.mutations.issueInstallState `returnUrl`), so it is decided per
// install by whoever starts one, instead of being frozen at App creation.
// That is why `appUrl` is gone from this manifest.
//
// `visibility` decides GitHub's `public` flag, and getting it wrong is a silent
// dead end rather than an error:
//
//   private  only the account that CREATED the App may install it. Correct for
//            self-host — one operator, one account, and the tighter setting.
//   public   any account may install it. REQUIRED for a hosted deployment,
//            because `githubAppCredentials` is a singleton: one App serves every
//            customer org, so those orgs must be able to install it. A private
//            App on hosted looks fine right up until a customer clicks "Install
//            on GitHub" and GitHub simply won't offer their org.
//
// This used to be hard-coded `false` while the storage was already a singleton —
// the manifest was written for bring-your-own and the storage for the vendor
// model, which is the contradiction the provisioning gate settled.
export function buildAppManifest(opts: {
  siteBase: string;
  homepageUrl: string;
  name: string;
  visibility: "public" | "private";
}): AppManifest {
  return {
    name: opts.name,
    url: opts.homepageUrl,
    hook_attributes: { url: `${opts.siteBase}/webhook/github` },
    redirect_url: `${opts.siteBase}/integrations/github/app-manifest-callback`,
    setup_url: `${opts.siteBase}/integrations/github/install-callback`,
    public: opts.visibility === "public",
    default_permissions: {
      pull_requests: "write",
      contents: "read",
      metadata: "read",
    },
    // Only OPT-IN webhook events go here, and each must be covered by a
    // permission above (pull_request ← pull_requests). The `installation` and
    // `installation_repositories` events are App-management events GitHub
    // delivers to every App automatically — listing them in a manifest is
    // rejected ("Invalid GitHub App configuration"), so they're intentionally
    // omitted; the webhook dispatcher still receives them.
    default_events: ["pull_request"],
  };
}
